import { useState, useCallback } from "react";
import { api } from "../api/api"; // Ajuste o caminho de acordo com a sua instância do Axios

export const useAgendarAtendimento = () => {
  const [agendamentoExistente, setAgendamentoExistente] = useState(null);
  const [proximosAgendamentos, setProximosAgendamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 🔍 Busca agendamento em aberto para o cliente (GET /cliente/:clienteId)
  const buscarAgendamentoPorCliente = useCallback(async (clienteId) => {
    if (!clienteId) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/agendaratendimento/cliente/${clienteId}`);
      setAgendamentoExistente(response.data);
      return response.data;
    } catch (err) {
      const mensagemErro =
        err.response?.data?.erro || "Erro ao buscar agendamento em aberto.";
      setError(mensagemErro);
      setAgendamentoExistente(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 📅 Busca agendamentos dos próximos dias (GET /agendaratendimento/proximos)
  const buscarProximosAgendamentos = useCallback(async (dias = 7) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/agendaratendimento/proximos?dias=${dias}`);
      setProximosAgendamentos(response.data || []);
      return response.data;
    } catch (err) {
      const mensagemErro =
        err.response?.data?.erro || "Erro ao buscar agendamentos dos próximos dias.";
      setError(mensagemErro);
      setProximosAgendamentos([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // 🗓️ Realiza o agendamento futuro (POST /agendar)
  const agendar = async (dadosAgendamento) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post("/agendaratendimento/agendar", dadosAgendamento);
      return response.data;
    } catch (err) {
      const mensagemErro =
        err.response?.data?.erro || "Erro ao agendar atendimento.";
      setError(mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  // ✏️ Atualiza agendamento existente
  const atualizar = async (id, dadosAgendamento) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.put(`/agendaratendimento/${id}`, dadosAgendamento);
      setAgendamentoExistente(response.data);
      return response.data;
    } catch (err) {
      const mensagemErro =
        err.response?.data?.erro || "Erro ao atualizar agendamento.";
      setError(mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Marca o agendamento como 'concluido'
  const concluirAgendamento = async (id) => {
    return await atualizar(id, { status: "concluido" });
  };

  // 🗑️ Exclui agendamento (DELETE /agendaratendimento/:id)
  const excluir = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.delete(`/agendaratendimento/${id}`);
      return response.data;
    } catch (err) {
      const mensagemErro =
        err.response?.data?.erro || "Erro ao excluir agendamento.";
      setError(mensagemErro);
      throw new Error(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  // 🧹 Reseta o estado local do agendamento encontrado
  const limparAgendamentoExistente = () => {
    setAgendamentoExistente(null);
    setError(null);
  };

  return {
    agendamentoExistente,
    proximosAgendamentos,
    loading,
    error,
    buscarAgendamentoPorCliente,
    buscarProximosAgendamentos,
    agendar,
    atualizar,
    concluirAgendamento,
    excluir,
    limparAgendamentoExistente
  };
};

export default useAgendarAtendimento;